/* eslint-disable no-shadow */
/* eslint-disable no-use-before-define */
import { propOr, prop } from 'ramda';
import { takeEvery, select, put } from 'redux-saga/effects';
import {
  READY,
  REQUEST_PLAYTIME,
  BACKEND_PLAYTIME,
  SET_CHAPTER,
  DISABLE_GHOST_MODE,
  PREVIOUS_CHAPTER,
  NEXT_CHAPTER,
  SHOW_PREV_CHAPTERS,
  SHOW_NEXT_CHAPTERS,
} from '@/store/actions/types';
import * as config from '@/config';
import { toPlayerTime } from '@/utils/time';
import {
  chapterIsShown, getNextPageIndex, getPrevPageIndex, getShownList,
} from '@/utils/chapters';
import * as chapter from '../actions/chapters';
import { requestPlaytime } from '../actions/timepiece';

export const chaptersSaga = ({
  selectDuration,
  selectPlaytime,
  selectCurrentChapter,
  selectChapterList,
  selectOffset,
  selectShowPrevButton,
  selectShowNextButton,
}) => function* saga() {
  yield takeEvery(READY, initChapters, {
    selectDuration,
    selectPlaytime,
    selectCurrentChapter,
    selectChapterList,
  });
  yield takeEvery(REQUEST_PLAYTIME, chapterUpdate);
  yield takeEvery(BACKEND_PLAYTIME, chapterUpdate);
  yield takeEvery(SET_CHAPTER, setChapter, {
    selectDuration,
    selectPlaytime,
    selectCurrentChapter,
    selectChapterList,
    selectOffset,
    selectShowPrevButton,
    selectShowNextButton,
  });
  yield takeEvery(DISABLE_GHOST_MODE, resetChapter, {
    selectDuration,
    selectPlaytime,
    selectCurrentChapter,
    selectChapterList,
  });
  yield takeEvery(PREVIOUS_CHAPTER, previousChapter, {
    selectDuration,
    selectPlaytime,
    selectCurrentChapter,
    selectChapterList,
  });
  yield takeEvery(NEXT_CHAPTER, nextChapter, {
    selectDuration,
    selectPlaytime,
    selectCurrentChapter,
    selectChapterList,
  });
  yield takeEvery(SHOW_PREV_CHAPTERS, showPrevChapters, {
    selectChapterList,
    selectOffset,
    selectShowPrevButton,
  });
  yield takeEvery(SHOW_NEXT_CHAPTERS, showNextChapters, {
    selectChapterList,
    selectOffset,
    selectShowNextButton,
  });
};

export function* chapterUpdate({ payload }) {
  yield put(chapter.updateChapter(payload));
}

export function* setChapter({
  selectCurrentChapter, selectChapterList, selectOffset, selectShowPrevButton, selectShowNextButton,
}) {
  const current = yield select(selectCurrentChapter);
  yield put(requestPlaytime(current.start));
  const chapters = yield select(selectChapterList);
  const offset = (yield select(selectOffset));
  const showPrevButton = yield select(selectShowPrevButton);
  const showNextButton = yield select(selectShowNextButton);
  const currentIndex = current.index - 1;

  const newIsShown = chapterIsShown(currentIndex, offset, showPrevButton, showNextButton);

  if (!newIsShown) {
    const newIndex = currentIndex <= offset ? getPrevPageIndex(currentIndex, showPrevButton) : currentIndex;
    yield put(chapter.setShownChapterList(getShownList(chapters, newIndex)));
  }
}

export function* resetChapter({ selectPlaytime }) {
  const playtime = yield select(selectPlaytime);
  yield put(chapter.updateChapter(playtime));
}

export function* previousChapter({ selectPlaytime, selectCurrentChapter }) {
  const playtime = yield select(selectPlaytime);
  const { start, index } = yield select(selectCurrentChapter);

  if (playtime - start <= 1000) {
    yield put(chapter.setChapter(index - 2 < 0 ? 0 : index - 2));
  } else {
    yield put(chapter.setChapter(index - 1 < 0 ? 0 : index - 1));
  }
}

export function* nextChapter({ selectChapterList, selectCurrentChapter }) {
  const chapters = yield select(selectChapterList);
  const { index } = yield select(selectCurrentChapter);
  yield put(chapter.setChapter(index > chapters.length - 1 ? chapters.length - 1 : index));
}

export function* showNextChapters({ selectChapterList, selectOffset, selectShowNextButton }) {
  const chapters = yield select(selectChapterList);
  const offset = yield select(selectOffset);
  const showNextButton = yield select(selectShowNextButton);
  yield put(chapter.setShownChapterList(getShownList(chapters, getNextPageIndex(offset, showNextButton))));
}

export function* showPrevChapters({ selectChapterList, selectOffset, selectShowPrevButton }) {
  const chapters = yield select(selectChapterList);
  const offset = yield select(selectOffset);
  const showPrevButton = yield select(selectShowPrevButton);
  yield put(chapter.setShownChapterList(getShownList(chapters, getPrevPageIndex(offset, showPrevButton))));
}

export function* initChapters({ selectDuration }, { payload }) {
  const chapters = config.chapters(payload);
  const duration = yield select(selectDuration);

  const state = chapters.reduce((result, chapter, index, chapters) => {
    const end = propOr({ start: duration }, index + 1, chapters);

    return [
      ...result,
      {
        index: index + 1,
        start: toPlayerTime(chapter.start),
        end: toPlayerTime(end.start),
        title: prop('title', chapter),
        image: prop('image', chapter),
      },
    ];
  }, []);
  yield put(chapter.setChapters(state));
  yield put(chapter.setShownChapterList(getShownList(chapters, 0)));
}
